import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import { apiGet } from '../lib/api_v2';
import db, { DEXIE_IS_LOADING } from '../lib/db';
import { directMessageWorkspaceSummary, storeDirectMessagePayload } from '../lib/direct_messages_store';
import { currentProfileId, currentUserId, setMetaContent } from '../lib/meta';
import { dispatchWorkspaceIdScope } from '../lib/workspaceIdScope';

import LoadingWrapper from '../components/shared/LoadingWrapper';
import { buildEntitySnapshots } from './EntitySnapshotContext';
import { stabilizeEntitySnapshots } from './entitySnapshotIdentity';
import { cacheRouteWorkspaceBundle, cachedRouteWorkspaceBundle } from './routeWorkspaceCache';

const RouteWorkspaceContext = createContext(null);

function routeWorkspaceBundleFromCreatedWorkspace(payload = {}) {
  const workspaceId = payload.workspace?.id ? String(payload.workspace.id) : null;
  if (!workspaceId) return null;

  const workspace = {
    ...payload.workspace,
    id: workspaceId,
    main_channel_id: payload.workspace.main_channel_id ? String(payload.workspace.main_channel_id) : null,
  };
  const profileWorkspaceId = payload.profile?.workspace_id ? String(payload.profile.workspace_id) : null;
  const profile =
    payload.profile?.id && profileWorkspaceId === workspaceId
      ? {
          ...payload.profile,
          id: String(payload.profile.id),
          workspace_id: profileWorkspaceId,
        }
      : null;
  const channelWorkspaceId = payload.channel?.workspace_id ? String(payload.channel.workspace_id) : null;
  const channel =
    payload.channel?.id && channelWorkspaceId === workspaceId
      ? {
          ...payload.channel,
          id: String(payload.channel.id),
          workspace_id: channelWorkspaceId,
        }
      : null;

  return {
    workspaceId,
    workspace,
    profile,
    user: payload.user || null,
    entitySnapshots: buildEntitySnapshots(workspaceId, profile ? [profile] : [], channel ? [channel] : [], []),
  };
}

export function primeCreatedWorkspaceRoute(payload) {
  cacheRouteWorkspaceBundle(routeWorkspaceBundleFromCreatedWorkspace(payload));
}

function useCurrentUser() {
  const userId = useMemo(() => currentUserId(), []);
  return useLiveQuery(() => (userId ? db.users.get(userId) : null), [userId], userId ? DEXIE_IS_LOADING : null);
}

function useEntitySnapshots(workspaceId, includeEntitySnapshots) {
  const stabilizedResultRef = useRef({ workspaceId: undefined, input: null, entitySnapshots: null });
  if (stabilizedResultRef.current.workspaceId !== workspaceId || !stabilizedResultRef.current.entitySnapshots) {
    const cachedEntitySnapshots = cachedRouteWorkspaceBundle(workspaceId, {
      requireProfile: false,
      includeEntitySnapshots: true,
    })?.entitySnapshots;
    stabilizedResultRef.current = { workspaceId, input: null, entitySnapshots: cachedEntitySnapshots || null };
  }

  const entitySnapshotResult = useLiveQuery(
    async () => {
      if (!includeEntitySnapshots) return null;
      if (!workspaceId) return buildEntitySnapshots(null, [], [], []);

      const profiles = await db.profiles.where('workspace_id').equals(workspaceId).toArray();
      const channels = await db.channels.where('workspace_id').equals(workspaceId).toArray();
      // All emojis, not just this workspace's: members can use custom emojis from
      // their other chats, and rendering resolves them from these snapshots.
      // Current chat first so shortcode collisions resolve to it.
      const allEmojis = await db.emojis.toArray();
      const emojis = [...allEmojis.filter((e) => e.workspace_id === workspaceId), ...allEmojis.filter((e) => e.workspace_id !== workspaceId)];

      return buildEntitySnapshots(workspaceId, profiles, channels, emojis);
    },
    [includeEntitySnapshots, workspaceId],
    includeEntitySnapshots ? DEXIE_IS_LOADING : null,
  );

  let entitySnapshots = null;
  if (includeEntitySnapshots && entitySnapshotResult !== DEXIE_IS_LOADING && entitySnapshotResult?.workspace_id === workspaceId) {
    // Fingerprinting is only worth paying once per liveQuery emission; renders
    // in between reuse the stabilized result for the same input object.
    if (stabilizedResultRef.current.input === entitySnapshotResult) {
      entitySnapshots = stabilizedResultRef.current.entitySnapshots;
    } else {
      entitySnapshots = stabilizeEntitySnapshots(stabilizedResultRef.current.entitySnapshots, entitySnapshotResult);
      stabilizedResultRef.current = { workspaceId, input: entitySnapshotResult, entitySnapshots };
    }
  }

  return { entitySnapshots, loaded: entitySnapshotResult !== DEXIE_IS_LOADING };
}

// Sole writer of the workspace/profile meta tags. Out-of-tree consumers
// (api.js interceptor, Stimulus, shared components in other apps) still read
// these via `lib/meta.js`, and event-driven consumers listen for the workspace
// scope event; keeping both in sync here is how a client-side workspace switch
// propagates to non-wouter code. Required-profile routes must not publish a
// transient null profile during hydration; keep the last resolved profile in
// meta/event scope until the next one is ready.
function useRouteScopeMeta({ workspaceId, profileId, requireProfile }) {
  useEffect(() => {
    setMetaContent('current_workspace:id', workspaceId);

    const scopedProfileId = profileId || (requireProfile ? currentProfileId() : null);
    if (profileId || !requireProfile) {
      setMetaContent('current_profile:id', profileId || null);
    }
    if (requireProfile && !scopedProfileId) return;

    dispatchWorkspaceIdScope({
      workspaceId,
      profileId: scopedProfileId,
    });
  }, [workspaceId, profileId, requireProfile]);
}

export const RouteWorkspaceProvider = ({
  workspaceId: rawWorkspaceId,
  requireProfile = true,
  includeEntitySnapshots = false,
  loadingFallback = <LoadingWrapper />,
  children,
}) => {
  // Normalize once so every Dexie lookup below uses the same string-keyed form.
  const workspaceId = rawWorkspaceId ? String(rawWorkspaceId) : null;
  const cachedBundleForLookup = cachedRouteWorkspaceBundle(workspaceId, { requireProfile: false });
  const cachedBundle = cachedRouteWorkspaceBundle(workspaceId, { requireProfile, includeEntitySnapshots });

  const workspaceResult = useLiveQuery(() => (workspaceId ? db.workspaces.get(workspaceId) : null), [workspaceId], DEXIE_IS_LOADING);
  // Shared-channel visitors may be shadow users with no `current_user:id`; tolerate a
  // missing userId by letting `useCurrentUser` skip the query (matchedProfile stays
  // null, which is fine when `requireProfile={false}`).
  const userResult = useCurrentUser();
  const userForProfileLookup = userResult === DEXIE_IS_LOADING ? cachedBundleForLookup?.user : userResult;

  const ownedProfileIds = Array.isArray(userForProfileLookup?.whoami) ? userForProfileLookup.whoami.map(String) : [];
  const ownedProfileIdsKey = ownedProfileIds.join(':');

  const matchedProfileResult = useLiveQuery(
    async () => {
      if (!workspaceId || ownedProfileIds.length === 0) return null;
      const candidates = await Promise.all(ownedProfileIds.map((id) => db.profiles.get([workspaceId, id])));
      return candidates.find(Boolean) || null;
    },
    [workspaceId, ownedProfileIdsKey],
    DEXIE_IS_LOADING,
  );

  const workspaceMatchesRoute = workspaceResult !== DEXIE_IS_LOADING && !!workspaceResult && String(workspaceResult.id) === workspaceId;
  const matchedProfile =
    matchedProfileResult !== DEXIE_IS_LOADING &&
    matchedProfileResult?.workspace_id &&
    String(matchedProfileResult.workspace_id) === workspaceId
      ? matchedProfileResult
      : null;
  // Treat "not found in Dexie yet" differently from "loaded for another route";
  // the former keeps the fallback up while sync has a chance to populate rows.
  const matchedProfileReady =
    ownedProfileIds.length === 0 ||
    (matchedProfile && String(matchedProfile.workspace_id) === workspaceId) ||
    (matchedProfileResult !== DEXIE_IS_LOADING && !matchedProfileResult);
  const hydrated = workspaceMatchesRoute && userResult !== DEXIE_IS_LOADING && matchedProfileReady;

  const { entitySnapshots, loaded: entitySnapshotsLoaded } = useEntitySnapshots(workspaceId, includeEntitySnapshots);
  const liveBundle = useMemo(() => {
    if (!hydrated) return null;
    if (requireProfile && !matchedProfile) return null;
    return {
      workspaceId,
      workspace: workspaceResult,
      profile: matchedProfile,
      user: userResult,
      entitySnapshots,
    };
  }, [entitySnapshots, hydrated, matchedProfile, requireProfile, userResult, workspaceId, workspaceResult]);

  const fullyHydratedLiveBundle =
    liveBundle && entitySnapshotsLoaded && (!includeEntitySnapshots || liveBundle.entitySnapshots) ? liveBundle : null;
  if (fullyHydratedLiveBundle) cacheRouteWorkspaceBundle(fullyHydratedLiveBundle);

  // useLiveQuery retains the previous dependency result while the new query runs.
  // Keep one complete workspace bundle selected until every requested piece matches.
  const routeQueriesHydrating =
    workspaceResult === DEXIE_IS_LOADING ||
    (!!workspaceResult && !workspaceMatchesRoute) ||
    userResult === DEXIE_IS_LOADING ||
    matchedProfileResult === DEXIE_IS_LOADING ||
    (!!matchedProfileResult && !matchedProfile) ||
    (includeEntitySnapshots && !entitySnapshots);
  const routeBundle = fullyHydratedLiveBundle || (routeQueriesHydrating && cachedBundle) || liveBundle;

  const value = useMemo(() => {
    if (!routeBundle) return null;

    const profileId = routeBundle.profile ? String(routeBundle.profile.id) : null;
    return {
      workspaceId: routeBundle.workspaceId,
      workspace: routeBundle.workspace,
      mainChannelId: routeBundle.workspace?.main_channel_id ? String(routeBundle.workspace.main_channel_id) : null,
      profileId,
      profile: routeBundle.profile,
      requiresProfile: requireProfile,
      user: routeBundle.user,
      entitySnapshots: includeEntitySnapshots ? routeBundle.entitySnapshots : null,
    };
  }, [includeEntitySnapshots, requireProfile, routeBundle]);
  useRouteScopeMeta({ workspaceId: value?.workspaceId || workspaceId, profileId: value?.profileId || null, requireProfile });

  if (!value) return loadingFallback;

  return <RouteWorkspaceContext.Provider value={value}>{children}</RouteWorkspaceContext.Provider>;
};

export const DirectMessageRouteProvider = ({
  directMessageId: rawDirectMessageId,
  requireProfile = true,
  includeEntitySnapshots = false,
  loadingFallback = <LoadingWrapper />,
  children,
}) => {
  const directMessageId = rawDirectMessageId ? String(rawDirectMessageId) : null;
  const [fetchFailed, setFetchFailed] = useState(false);
  const [resourceHydratedDirectMessageId, setResourceHydratedDirectMessageId] = useState(null);

  const directMessage = useLiveQuery(
    () => (directMessageId ? db.direct_messages.get(directMessageId) : null),
    [directMessageId],
    DEXIE_IS_LOADING,
  );

  // Dexie hydration uses a sentinel, not a boolean; we only need to know whether the
  // initial lookup has resolved (row found or confirmed absent) — re-running the
  // effect on every routine row update (e.g. `last_message_sort_order` bumps) just
  // churns closures.
  const directMessageDexieResolved = directMessage !== DEXIE_IS_LOADING;
  useEffect(() => {
    // The rail/index payload is enough to route immediately, but the show payload
    // also hydrates DM-scoped widgets, board items, and widget builder chats.
    if (!directMessageId || !directMessageDexieResolved || fetchFailed) return;
    if (resourceHydratedDirectMessageId === directMessageId) return;

    let cancelled = false;
    let retryTimer = null;
    async function fetchDirectMessage() {
      try {
        const response = await apiGet(`/direct_messages/${directMessageId}`);
        const data = await response.json();
        if (!(response.ok && data.ok && data.data?.direct_message)) throw new Error('direct_message_not_found');
        if (!cancelled) {
          await storeDirectMessagePayload(data.data.direct_message);
          setResourceHydratedDirectMessageId(directMessageId);
        }
      } catch {
        if (cancelled) return;
        setFetchFailed(true);
        // A transient failure shouldn't permanently strand the route on the loading
        // fallback or leave widgets/board items un-hydrated; clear the flag after a
        // short delay so the effect retries.
        retryTimer = setTimeout(() => {
          if (!cancelled) setFetchFailed(false);
        }, 5000);
      }
    }

    fetchDirectMessage();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [directMessageDexieResolved, directMessageId, fetchFailed, resourceHydratedDirectMessageId]);

  useEffect(() => {
    setFetchFailed(false);
    setResourceHydratedDirectMessageId(null);
  }, [directMessageId]);

  const workspaceId = directMessage?.workspace_id ? String(directMessage.workspace_id) : null;
  const profileIdFromDirectMessage = directMessage?.current_profile_id ? String(directMessage.current_profile_id) : null;
  const workspace = useMemo(() => directMessageWorkspaceSummary(directMessage), [directMessage]);

  const currentProfile = useLiveQuery(
    () => (workspaceId && profileIdFromDirectMessage ? db.profiles.get([workspaceId, profileIdFromDirectMessage]) : null),
    [workspaceId, profileIdFromDirectMessage],
    profileIdFromDirectMessage ? DEXIE_IS_LOADING : null,
  );

  const user = useCurrentUser();
  // Dexie may not have written the profile row yet on first hydration; fall back to
  // the embedded `current_profile` from the index/show payload until it does.
  const resolvedProfile =
    currentProfile === DEXIE_IS_LOADING ? directMessage?.current_profile || null : currentProfile || directMessage?.current_profile || null;
  const profileId = resolvedProfile?.id ? String(resolvedProfile.id) : profileIdFromDirectMessage;
  const hydrated = directMessageDexieResolved && !!directMessage && !!workspace && user !== DEXIE_IS_LOADING;

  const { entitySnapshots } = useEntitySnapshots(workspaceId, includeEntitySnapshots);
  useRouteScopeMeta({ workspaceId, profileId, requireProfile });

  const value = useMemo(() => {
    if (!hydrated) return null;
    if (requireProfile && !resolvedProfile) return null;

    return {
      workspaceId,
      workspace,
      mainChannelId: directMessage?.main_channel_id ? String(directMessage.main_channel_id) : null,
      profileId,
      profile: resolvedProfile,
      requiresProfile: requireProfile,
      user,
      entitySnapshots,
      directMessage,
      directMessageId,
      isDirectMessage: true,
    };
  }, [
    directMessage,
    directMessageId,
    entitySnapshots,
    hydrated,
    profileId,
    requireProfile,
    resolvedProfile,
    user,
    workspace,
    workspaceId,
  ]);

  if (!value) return loadingFallback;

  return <RouteWorkspaceContext.Provider value={value}>{children}</RouteWorkspaceContext.Provider>;
};

export const useRouteWorkspace = () => {
  const ctx = useContext(RouteWorkspaceContext);
  if (!ctx) throw new Error('useRouteWorkspace must be used inside a RouteWorkspaceProvider');
  return ctx;
};

export const useOptionalRouteWorkspace = () => useContext(RouteWorkspaceContext);
